import React, { useState, FC } from "react";
import { useMount } from "react-use";
import useStore from "stores/index";
import SJRModal from "js/components/SchoolJourneyReport/components/SJRModal";
import ScreenshotHistoryModal from "js/components/ScreenshotHistoryModal";
import { getFeatureFlags } from "stores/featureTogglesStore";
import { getClasswizeConfig } from "stores/classwizeConfigStore";
import { getSubgroups } from "js/api/classApi";
import { FeatureToggles } from "@/api/linewizeFilterFeatureFlags";
import { UserInfo } from "js/api/authApi";
import { ActionsDropdownItem, ActionsMenu } from "js/components/ActionsMenu";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import ErrorBoundary from "@/components/ErrorBoundary";

type Props = {
	students: Partial<UserInfo>[];
};

export const ReportsDropdown: FC<Props> = ({ students }) => {
	const [showSJRModal, setShowSJRModal] = useState(false);
	const [showSHModal, setShowSHModal] = useState(false);
	const [enableReporting, setEnableReporting] = useState(false);

	// TODO: Migrate the stores to use TypeScript for increased type-safety
	const classwizeConfig = getClasswizeConfig();
	const featureFlags = getFeatureFlags(useStore.getState()) as FeatureToggles;

	const getConfigForReporting = async (): Promise<boolean> => {
		const reportingEnable = classwizeConfig.classroom_teachers_allow_reporting_access;
		const reportingType = classwizeConfig.teachers_reporting_type;

		const configuredReportingGroups = classwizeConfig.teachers_reporting_groups;
		const teacherGroups = useUserSessionStore.getState().getUsersGroups();

		//Checks if reporting has been enabled and type of reporting
		if (!reportingEnable) {
			return false;
		} else {
			if (reportingType === "" || reportingType === undefined) {
				return true;
			}
			if (!teacherGroups || teacherGroups?.length === 0) {
				return false;
			}
			if (!configuredReportingGroups || configuredReportingGroups?.length === 0) {
				return reportingType === "allowList" ? false : true;
			}

			let reportingGroups: Array<string> = [];
			await getSubgroups(configuredReportingGroups).then((data: Array<string>) => {
				if (data) {
					reportingGroups = [...configuredReportingGroups, ...data];
				} else {
					reportingGroups = [...configuredReportingGroups];
				}
			});

			if (reportingType === "allowList") {
				return reportingGroups.some((r) => teacherGroups.includes(r));
			}
			if (reportingType === "blockList") {
				return !reportingGroups.some((r) => teacherGroups.includes(r));
			}
			return false;
		}
	};

	useMount(() => {
		getConfigForReporting()
			.then((enable: boolean) => setEnableReporting(enable))
			.catch((e: Error) => console.error(e));
	});

	const dropDownItems: ActionsDropdownItem[] = [
		{
			icon: "fa-camera",
			label: "Screenshot History",
			show: featureFlags && featureFlags["shr-enabled"],
			disabled: false,
			action: () => setShowSHModal(true),
			dataTestId: "shr-button",
		},
		{
			icon: "fa-file-lines",
			label: "Student Journey",
			show: true,
			disabled: false,
			action: () => setShowSJRModal(true),
			dataTestId: "sjr-button",
		},
	];

	if (!enableReporting) return null;

	return (
		<ErrorBoundary>
			<ActionsMenu title="Reports" items={dropDownItems} dataTestId="report-dropdown" />
			{showSHModal && <ScreenshotHistoryModal students={students} handleClose={() => setShowSHModal(false)} />}
			{showSJRModal && <SJRModal students={students} handleClose={() => setShowSJRModal(false)} />}
		</ErrorBoundary>
	);
};

export default ReportsDropdown;
